import type { CaptionCue } from "./captions";

export const AUDIO_CHANNELS = ["music", "voice", "sfx"] as const;

export type AudioChannel = (typeof AUDIO_CHANNELS)[number];

export type AudioMimeType = "audio/mpeg" | "audio/ogg";

export type NonEmptyReadonlyArray<T> = readonly [T, ...T[]];

export interface AudioSource {
  readonly src: string;
  readonly type?: AudioMimeType;
}

interface BaseTrackDefinition {
  readonly sources: NonEmptyReadonlyArray<AudioSource>;
  readonly gain?: number;
}

export interface MusicTrackDefinition extends BaseTrackDefinition {
  readonly loop?: boolean;
}

export interface VoiceTrackDefinition extends BaseTrackDefinition {
  readonly captions: NonEmptyReadonlyArray<CaptionCue>;
  readonly fallbackCaption?: string;
}

export type SfxTrackDefinition = BaseTrackDefinition;

export interface AudioCatalog {
  readonly music: Readonly<Record<string, MusicTrackDefinition>>;
  readonly voice: Readonly<Record<string, VoiceTrackDefinition>>;
  readonly sfx: Readonly<Record<string, SfxTrackDefinition>>;
}

export type AudioTrackId<
  TCatalog extends AudioCatalog,
  TChannel extends AudioChannel
> = Extract<keyof TCatalog[TChannel], string>;

export function defineAudioCatalog<const TCatalog extends AudioCatalog>(
  catalog: TCatalog
): TCatalog {
  return catalog;
}

export const EMPTY_AUDIO_CATALOG: AudioCatalog = defineAudioCatalog({
  music: {},
  voice: {},
  sfx: {}
});
